import {
  FileXls,
  Table,
  Funnel,
  ArrowsDownUp,
  Calculator,
  Copy,
  ChartBar,
  SquaresFour,
  ArrowsMerge,
  Stack,
  ArrowsLeftRight,
  DownloadSimple,
  ArrowsClockwise,
  Trash,
  QrCodeIcon,
  CaretDown,
  DotsNineIcon,
  Rows,
  GitBranch,
} from '@phosphor-icons/react';
import type { FC } from 'react';

export type PhosphorIconProps = {
  className?: string;
  weight?: 'thin' | 'light' | 'regular' | 'bold' | 'fill' | 'duotone';
};

export type NodeIconComponent = FC<PhosphorIconProps>;

export interface NodeMeta {
  /** i18n key for label, e.g. "nodes.nodeTypes.load_excel.label" */
  labelKey: string;
  /** i18n key for description, e.g. "nodes.nodeTypes.load_excel.desc" */
  descKey: string;
  icon: NodeIconComponent;
  color: string;
  bg: string;
  border: string;
  /** Maximum number of input connections allowed. undefined means unlimited */
  maxInputs?: number;
}

export const NODE_META: Record<string, NodeMeta> = {
  // 数据源节点：无输入
  load_excel:   { labelKey: 'nodes.nodeTypes.load_excel.label',   descKey: 'nodes.nodeTypes.load_excel.desc',   icon: FileXls,        color: 'text-blue-600',   bg: 'bg-blue-50',   border: 'border-blue-200' },
  load_csv:     { labelKey: 'nodes.nodeTypes.load_csv.label',     descKey: 'nodes.nodeTypes.load_csv.desc',     icon: Table,          color: 'text-sky-600',    bg: 'bg-sky-50',    border: 'border-sky-200' },
  load_table:   { labelKey: 'nodes.nodeTypes.load_table.label',   descKey: 'nodes.nodeTypes.load_table.desc',   icon: Table,          color: 'text-cyan-600',   bg: 'bg-cyan-50',   border: 'border-cyan-200' },
  // online_sheet: { labelKey: 'nodes.nodeTypes.online_sheet.label', descKey: 'nodes.nodeTypes.online_sheet.desc', icon: Cloud,          color: 'text-indigo-600', bg: 'bg-indigo-50', border: 'border-indigo-200' },
  
  // 单输入节点：筛选、排序、计算列、映射转换、去重、删除列、分组、聚合统计
  filter:       { labelKey: 'nodes.nodeTypes.filter.label',       descKey: 'nodes.nodeTypes.filter.desc',       icon: Funnel,         color: 'text-purple-600', bg: 'bg-purple-50', border: 'border-purple-200',  maxInputs: 1 },
  sort:         { labelKey: 'nodes.nodeTypes.sort.label',         descKey: 'nodes.nodeTypes.sort.desc',         icon: ArrowsDownUp,   color: 'text-purple-600', bg: 'bg-purple-50', border: 'border-purple-200',  maxInputs: 1 },
  compute:      { labelKey: 'nodes.nodeTypes.compute.label',      descKey: 'nodes.nodeTypes.compute.desc',      icon: Calculator,     color: 'text-purple-600', bg: 'bg-purple-50', border: 'border-purple-200',  maxInputs: 1 },
  transform:    { labelKey: 'nodes.nodeTypes.transform.label',    descKey: 'nodes.nodeTypes.transform.desc',    icon: ArrowsClockwise,color: 'text-purple-600', bg: 'bg-purple-50', border: 'border-purple-200',  maxInputs: 1 },
  dedup:        { labelKey: 'nodes.nodeTypes.dedup.label',        descKey: 'nodes.nodeTypes.dedup.desc',        icon: Copy,           color: 'text-purple-600', bg: 'bg-purple-50', border: 'border-purple-200',  maxInputs: 1 },
  drop: { labelKey: 'nodes.nodeTypes.drop.label', descKey: 'nodes.nodeTypes.drop.desc', icon: Trash,          color: 'text-purple-600', bg: 'bg-purple-50', border: 'border-purple-200',  maxInputs: 1 },
  data_normalization: { labelKey: 'nodes.nodeTypes.data_normalization.label', descKey: 'nodes.nodeTypes.data_normalization.desc', icon: Rows, color: 'text-purple-600', bg: 'bg-purple-50', border: 'border-purple-200', maxInputs: 1 },
  aggregate:    { labelKey: 'nodes.nodeTypes.aggregate.label',    descKey: 'nodes.nodeTypes.aggregate.desc',    icon: ChartBar,       color: 'text-rose-600',   bg: 'bg-rose-50',   border: 'border-rose-200', maxInputs: 1 },
  group_by:     { labelKey: 'nodes.nodeTypes.group_by.label',     descKey: 'nodes.nodeTypes.group_by.desc',     icon: SquaresFour,    color: 'text-pink-600',   bg: 'bg-pink-50',   border: 'border-pink-200', maxInputs: 1 },
  barcode:      { labelKey: 'nodes.nodeTypes.barcode.label',      descKey: 'nodes.nodeTypes.barcode.desc',      icon: QrCodeIcon,        color: 'text-purple-600', bg: 'bg-purple-50', border: 'border-purple-200', maxInputs: 1 },
  top_n:        { labelKey: 'nodes.nodeTypes.top_n.label',        descKey: 'nodes.nodeTypes.top_n.desc',        icon: CaretDown,        color: 'text-purple-600', bg: 'bg-purple-50', border: 'border-purple-200',  maxInputs: 1 },
  pivot:        { labelKey: 'nodes.nodeTypes.pivot.label',        descKey: 'nodes.nodeTypes.pivot.desc',        icon: DotsNineIcon, color: 'text-rose-600',   bg: 'bg-rose-50',   border: 'border-rose-200', maxInputs: 1 },
  conditional_column: { labelKey: 'nodes.nodeTypes.conditional_column.label', descKey: 'nodes.nodeTypes.conditional_column.desc', icon: GitBranch, color: 'text-purple-600', bg: 'bg-purple-50', border: 'border-purple-200', maxInputs: 1 },
  
  // 多输入节点：关联合并、差异对比（需要多个输入）
  join:         { labelKey: 'nodes.nodeTypes.join.label',         descKey: 'nodes.nodeTypes.join.desc',         icon: ArrowsMerge,    color: 'text-amber-600',  bg: 'bg-amber-50',  border: 'border-amber-200' },
  append:       { labelKey: 'nodes.nodeTypes.append.label',       descKey: 'nodes.nodeTypes.append.desc',       icon: Stack,          color: 'text-orange-600', bg: 'bg-orange-50', border: 'border-orange-200' },
  diff:         { labelKey: 'nodes.nodeTypes.diff.label',         descKey: 'nodes.nodeTypes.diff.desc',         icon: ArrowsLeftRight,color: 'text-violet-600', bg: 'bg-violet-50', border: 'border-violet-200' },
  
  // 输出节点：单输入
  export: { labelKey: 'nodes.nodeTypes.export.label', descKey: 'nodes.nodeTypes.export.desc', icon: DownloadSimple, color: 'text-zinc-600',   bg: 'bg-zinc-100',  border: 'border-zinc-200', maxInputs: 1 },
};

/** Multi-input nodes: can accept unlimited inputs */
export const MULTI_INPUT_TYPES = new Set(['join', 'append', 'diff']);

/** 仅包含在 NODE_META 中已定义的节点类型，保证节点库能正确渲染 */
export const NODE_PALETTE_CATEGORIES: { label: string; types: string[] }[] = [
  { label: '输入', types: ['load_table', 'online_sheet'] },
  { label: '转换', types: ['filter', 'conditional_column', 'sort', 'compute', 'transform', 'dedup', 'drop', 'data_normalization', 'top_n', 'barcode'] },
  { label: '聚合', types: ['aggregate', 'group_by', 'pivot'] },
  { label: '合并', types: ['join', 'append'] },
  { label: '对比', types: ['diff'] },
  { label: '输出', types: ['export'] },
];

/** Returns a hex color string for MiniMap node coloring */
export function getNodeColor(type: string): string {
  const colorMap: Record<string, string> = {
    load_excel: '#bfdbfe', load_csv: '#bae6fd', load_table: '#a5f3fc', online_sheet: '#c7d2fe', load_api: '#c7d2fe',
    // 转换节点统一使用 lime 色调
    filter: '#d9f99d', sort: '#d9f99d', compute: '#d9f99d', transform: '#d9f99d', dedup: '#d9f99d', drop: '#d9f99d', data_normalization: '#d9f99d', top_n: '#d9f99d', barcode: '#e9d5ff', conditional_column: '#d9f99d',
    aggregate: '#fecdd3', group_by: '#fbcfe8', pivot: '#fecdd3',
    join: '#fde68a', append: '#fed7aa',
    diff: '#ddd6fe',
    export: '#e4e4e7',
  };
  return colorMap[type] ?? '#e4e4e7';
}
